import { TFunction } from 'i18next';

import { InventoryExitReason } from 'src/Services/API/generated';

/** Same limits as the server. */
export const INVENTORY_PHOTOS_MAX = 5;

export const EXIT_REASONS: InventoryExitReason[] = [
  InventoryExitReason.Broken,
  InventoryExitReason.Given,
  InventoryExitReason.Sold,
  InventoryExitReason.ThrownAway,
  InventoryExitReason.LostOrStolen,
  InventoryExitReason.Other,
];

export const exitReasonLabel = (t: TFunction, reason: InventoryExitReason): string => {
  switch (reason) {
    case InventoryExitReason.Broken:
      return t('inventory.exitReasons.broken', 'Cassé ou détruit');
    case InventoryExitReason.Given:
      return t('inventory.exitReasons.given', 'Donné');
    case InventoryExitReason.Sold:
      return t('inventory.exitReasons.sold', 'Vendu');
    case InventoryExitReason.ThrownAway:
      return t('inventory.exitReasons.thrownAway', 'Jeté');
    case InventoryExitReason.LostOrStolen:
      return t('inventory.exitReasons.lostOrStolen', 'Perdu ou volé');
    case InventoryExitReason.Other:
      return t('inventory.exitReasons.other', 'Autre');
  }
};

/** Where a field of Lucien's card comes from: PHOTO | LABEL | VOICE. */
export const draftSourceLabel = (t: TFunction, source?: string): string | null => {
  switch (source) {
    case 'PHOTO':
      return t('inventory.sources.photo', 'Vu sur la photo');
    case 'LABEL':
      return t('inventory.sources.label', "Lu sur l'étiquette");
    case 'VOICE':
      return t('inventory.sources.voice', 'Dit à la voix');
    default:
      return null;
  }
};

/** 1 250 € — whole euros: an inventory is an estimate. */
export const formatEuros = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);

export const formatExitDate = (date: string): string =>
  new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/** Same rule as the server: replacement value minus wear since the purchase year. */
export const computeCurrentValue = (
  item: {
    replacementValue?: number | null;
    purchaseYear?: number | null;
    depreciationEnabled: boolean;
  },
  rule: { depreciationRatePercent: number; depreciationMaxPercent: number }
): number | null => {
  if (item.replacementValue === null || item.replacementValue === undefined) return null;
  if (!item.depreciationEnabled) return Math.round(item.replacementValue);
  if (item.purchaseYear === null || item.purchaseYear === undefined) return null;
  const age = Math.max(0, new Date().getFullYear() - item.purchaseYear);
  const wear = Math.min(rule.depreciationMaxPercent, age * rule.depreciationRatePercent);
  return Math.round((item.replacementValue * (100 - wear)) / 100);
};
